import "./App.css";
import React, { useContext, useState, useEffect } from "react";
import ReactGA from "react-ga4";
import MainLayout from "./layouts/MainLayout";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import ThemeCustomization from "./themes";
import SignIn from "./pages/SignIn/SignIn";
import { UserContext } from "./context/UserContext";
import i18n from "../src/i18n/config";
import { transitions, positions, Provider as AlertProvider } from "react-alert";
import Plans from "./pages/Plans/Plans";
import Joyride, { EVENTS } from "react-joyride";
import axios from "axios";
import { ADMIN_URL } from "./utils/constants";
import Logger from "./utils/Logger";
import { levels } from "./utils/Logger";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import AccountDeletion from "./pages/AccountDeletion/AccountDeletion";
// optional configuration
const options = {
	// you can also just use 'bottom center'
	position: positions.TOP_RIGHT,
	timeout: 5000,
	offset: "30px",
	// you can also just use 'scale'
	transition: transitions.SCALE,
	containerStyle: {
		zIndex: 9999,
	},
};

axios.defaults.headers.common["Accept-Language"] =
	i18n.language === "es" ||
	i18n.language.includes("es-") ||
	i18n.language.includes("-ES") ||
	i18n.language.includes("es_") ||
	i18n.language.includes("_ES") ||
	i18n.language === "ca" ||
	i18n.language === "eu" ||
	i18n.language === "gl"
		? "es"
		: "en";

// the style contains only the margin given as offset
// options contains all alert given options
// message is the alert message
// close is a function that closes the alert
const AlertTemplate = ({ style, options, message, close }) => (
	<div
		style={{
			backgroundColor: options.type === "error" ? "#eb4d4b" : "#43BD98",
			padding: "12px 20px",
			margin: 20,
			borderRadius: 4,
			color: "white",
			fontWeight: "bold",
		}}
	>
		{message}
	</div>
);

Logger("Start App", levels.INFO, {});

function App() {
	const { user, getUser } = useContext(UserContext);
	const { t } = useTranslation();
	const [run, setRun] = useState(false);
	const [onboardingStarted, setOnboardingStarted] = useState(false);
	const [onboardingFinished, setOnboardingFinished] = useState(false);
	const [goToQR, setGoToQR] = useState(false);
	const [goToNavigation, setGoToNavigation] = useState(false);
	const [goToAppearance, setGoToAppearance] = useState(false);
	const [goToPages, setGoToPages] = useState(false);
	const [goToHome, setGoToHome] = useState(false);
	const [goToPublish, setGoToPublish] = useState(false);
	const [step, setStep] = useState(0);
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));
	const [steps, setSteps] = useState([
		{
			target: "#code" + (isXs ? "-mobile" : ""),
			content: (
				<>
					{t("onboarding.code1")}
					<br />
					<br />
					{t("onboarding.code2")}
				</>
			),
			disableBeacon: true,
		},
		{
			target: "#qr",
			content: <>{t("onboarding.qr")}</>,
		},
		{
			target: "#navigation" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.navigation")}</>,
		},
		{
			target: "#drawer",
			content: <>{t("onboarding.drawer")}</>,
		},
		{
			target: "#navigation-save",
			content: <>{t("onboarding.navigationSave")}</>,
		},
		{
			target: "#appearance" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.appearance")}</>,
		},
		{
			target: "#main-color",
			content: <>{t("onboarding.mainColor")}</>,
		},
		{
			target: "#appearance-save",
			content: <>{t("onboarding.appearanceSave")}</>,
		},
		{
			target: "#pages" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.pages")}</>,
		},
		{
			target: "#pages-visible",
			content: <>{t("onboarding.visible")}</>,
		},
		{
			target: "#pages-header",
			content: <>{t("onboarding.pagesHeader")}</>,
		},
		{
			target: "#home" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.home")}</>,
		},
		{
			target: "#home-style1",
			content: <>{t("onboarding.homeStyle")}</>,
		},
		{
			target: "#publish" + (isXs ? "-mobile" : ""),
			content: <>{t("onboarding.publish")}</>,
		},
		{
			target: "#publish-message",
			content: <>{t("onboarding.publishMessage")}</>,
		},
		{
			target: "#admin",
			content: <>{t("onboarding.admin")}</>,
		},
	]);

	useEffect(() => {
		ReactGA.initialize("G-XTJXXMV0DX");
	}, []);

	return (
		<ThemeCustomization>
			<Joyride
				steps={steps}
				callback={async (data) => {
					const { action, index, status, type } = data;

					console.log(data);

					setStep(index);

					if (type === EVENTS.TOOLTIP && index === 0) {
						setGoToQR(true);
					}
					if (type === EVENTS.STEP_AFTER && index === 1) {
						setRun(false);
						setGoToNavigation(true);
					}
					if (type === EVENTS.STEP_AFTER && index === 4) {
						setRun(false);
						setGoToAppearance(true);
					}
					if (type === EVENTS.STEP_AFTER && index === 7) {
						setRun(false);
						setGoToPages(true);
					}
					if (type === EVENTS.STEP_AFTER && index === 9) {
						setRun(false);
						setGoToHome(true);
					}
					if (type === EVENTS.STEP_AFTER && index === 12) {
						setRun(false);
						setGoToPublish(true);
					}
					if (type === EVENTS.TOUR_END) {
						setRun(false);

						setOnboardingStarted(false);
						if (index === 15) {
							setOnboardingFinished(true);
						}

						try {
							await axios.post(
								ADMIN_URL + "adminUpdateOnboarding",
								{},
								{
									headers: {
										authorization: localStorage.getItem("token"),
									},
								}
							);
							await getUser();
						} catch (error) {
							console.log(error);
						}
					}
				}}
				locale={{
					back: t("onboarding.back"),
					close: t("onboarding.close"),
					last: t("onboarding.last"),
					next: t("onboarding.next"),
					open: t("onboarding.open"),
					skip: t("onboarding.skip"),
				}}
				continuous
				disableCloseOnEsc
				disableOverlayClose
				hideBackButton
				hideCloseButton
				run={run && onboardingStarted}
				scrollToFirstStep={false}
				scrollOffset={0}
				disableScrolling={step < 11}
				showProgress
				showSkipButton
				styles={{
					options: {
						zIndex: 10000,
						primaryColor: "#43BD98",
						fontSize: "12px",
					},
				}}
			/>
			<AlertProvider template={AlertTemplate} {...options}>
				{user === null || (user && !user.activatedAt) ? (
					<BrowserRouter>
						<Routes>
							<Route path="/" exact element={<SignIn />}></Route>
							<Route
								path="/account-deletion"
								exact
								element={<AccountDeletion />}
							></Route>
							<Route path="*" element={<Navigate to="/" replace />} />
						</Routes>
					</BrowserRouter>
				) : user &&
				  (!user.subscription ||
						(user.subscription &&
							!["active", "past_due", "trialing"].find(
								(subscriptionStatus) =>
									subscriptionStatus === user.subscription.status
							))) ? (
					<BrowserRouter>
						<Routes>
							<Route path="/plans" exact element={<Plans />}></Route>
							<Route
								path="/account-deletion"
								exact
								element={<AccountDeletion />}
							></Route>
							<Route path="*" element={<Navigate to="/plans" replace />} />
						</Routes>
					</BrowserRouter>
				) : (
					<MainLayout
						goToQR={goToQR}
						setGoToQR={setGoToQR}
						goToNavigation={goToNavigation}
						setGoToNavigation={setGoToNavigation}
						goToAppearance={goToAppearance}
						setGoToAppearance={setGoToAppearance}
						onboardingStarted={onboardingStarted}
						setOnboardingStarted={setOnboardingStarted}
						onboardingFinished={onboardingFinished}
						setOnboardingFinished={setOnboardingFinished}
						goToPages={goToPages}
						setGoToPages={setGoToPages}
						goToHome={goToHome}
						setGoToHome={setGoToHome}
						goToPublish={goToPublish}
						setGoToPublish={setGoToPublish}
						setRun={setRun}
					/>
				)}
			</AlertProvider>
		</ThemeCustomization>
	);
}

export default App;
